import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { isAllowEdit } from 'features/supplier/utils';

import CustomDropdown from '@components/CustomDropdown';
import { toastError, toastSuccess } from '@components/Toast';

import { getAllCountries } from '@store/reducers';
import { fetchAllCountry, fetchSupplierCityByCountry, fetchSupplierDetails } from '@store/supplier/action';

import ApiClient from '@lib/apiClient';

import { getShortName, isValidURL } from '@utils/Util';

const Location = ({ officeLocationInfo, factoryLocationInfo, supplierId }) => {
	const [officeLocation, setOfficeLocation] = useState(null);
	const [factoryLocation, setfactoryLocation] = useState(null);
	const [editOffice, setEditOffice] = useState(false);
	const [errors, setErrors] = useState({});
	const [locationData, setLocationData] = useState(null);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [, setCityId] = useState('');
	const [countryId, setCountryId] = useState('');

	const dispatch = useDispatch();

	useEffect(() => {
		setOfficeLocation(officeLocationInfo);
		setfactoryLocation(factoryLocationInfo);
	}, [officeLocationInfo, factoryLocationInfo]);

	let countries = useSelector(getAllCountries);

	const handleClose = () => {
		setEditOffice(false);
		dispatch(fetchSupplierDetails(supplierId));
		setErrors({});
	};

	const onEditLocation = (type) => {
		if (type === 'office') {
			if (officeLocationInfo?.countryResponse) {
				dispatch(fetchSupplierCityByCountry(officeLocationInfo?.countryResponse?.id));
			}
			setLocationData(officeLocation);
			setCountryId(officeLocation?.countryResponse?.id);
			setCityId(officeLocation?.cityResponse?.id);
		} else {
			if (factoryLocationInfo?.countryResponse) {
				dispatch(fetchSupplierCityByCountry(factoryLocationInfo?.countryResponse?.id));
			}
			setLocationData(factoryLocation);
			setCountryId(factoryLocationInfo?.countryResponse?.id);
			setCityId(factoryLocationInfo?.cityResponse?.id);
		}
		dispatch(fetchAllCountry());
		setEditOffice(true);
	};

	const onChangeCountry = (item) => {
		if (item.id) {
			dispatch(fetchSupplierCityByCountry(item.id));
			setLocationData({ ...locationData, countryResponse: item.id });
			setCountryId(item.id);
			setCityId('');
		} else {
			setLocationData({ ...locationData, countryResponse: '' });
			setLocationData({ ...locationData, cityResponse: '' });
			setCityId(null);
			setCountryId(null);
		}
	};

	const onChnageInfo = (e) => {
		setLocationData({ ...locationData, [e.target.name]: e.target.value });
	};

	const isValidForm = (body) => {
		if (body.countryId === '' || !body.countryId) {
			setErrors({ ...errors, countryError: 'Country is required.' });
			return false;
		}
		if (body.city === '' || !body.city) {
			setErrors({ ...errors, cityError: 'City is required' });
			return false;
		}
		if (body?.mapUrl) {
			if (!isValidURL(body.mapUrl)) {
				setErrors({ ...errors, validMapUrl: 'Valid location url is required' });
				return false;
			}
		}
		return true;
	};

	const onSubmit = async () => {
		setIsSubmitLoading(true);
		const body = {
			address: locationData?.address,
			mapUrl: locationData?.mapUrl,
			zip: locationData?.zip,
			state: locationData?.state,
			city: locationData?.city,
			countryId: countryId
		};

		const isValid = isValidForm(body);
		if (!isValid) {
			setIsSubmitLoading(false);
			return;
		}
		await ApiClient.PUT('updateLocation', body, locationData.id)
			.then(({ data }) => {
				setIsSubmitLoading(false);
				if (data && data.success) {
					toastSuccess(data.message);
					handleClose();
				}
			})
			.catch(({ response }) => {
				setIsSubmitLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	return (
		<>
			<div className="row" id="location-section">
				<div className="col-md-6 padding-col">
					<div className="location-details">
						<div className="location-heading mb-2">
							<span className="regular-16 weight-500 gray_dark_01">Office Location </span>
							{isAllowEdit() && (
								<img
									src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
									alt="edit"
									onClick={() => onEditLocation('office')}
								/>
							)}
						</div>
						<div className="full-details ">
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">Country</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">{officeLocation?.countryResponse?.name}</p>
							</div>
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">State</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">{officeLocation?.state}</p>
							</div>
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">City</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">{officeLocation?.city}</p>
							</div>
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">Address</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">{officeLocation?.address}</p>
							</div>
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">Map</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14 map-link">
									<a href={officeLocation?.mapUrl} target="_blank" rel="noreferrer">
										{getShortName(officeLocation?.mapUrl)}
									</a>
								</p>
							</div>
						</div>
					</div>
				</div>
				<div className="col-md-6 padding-col">
					<div className="location-details">
						<div className="location-heading mb-2">
							<span className="regular-16 weight-500 gray_dark_01">Factory Location</span>
							{isAllowEdit() && (
								<img
									src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
									alt="edit"
									onClick={() => onEditLocation('factory')}
								/>
							)}
						</div>
						<div className="full-details ">
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">Country</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">{factoryLocation?.countryResponse?.name}</p>
							</div>
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">State</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">{factoryLocation?.state}</p>
							</div>
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">City</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">{factoryLocation?.city}</p>
							</div>
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">Address</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">{factoryLocation?.address}</p>
							</div>
							<div className="info-details mb-2 d-flex">
								<p className="regular-14 gray_dark_02">Map</p>
								<p className="gray_dark_02">:</p>
								<p className="regular-14">
									<a href={factoryLocation?.mapUrl} target="_blank" rel="noreferrer">
										{factoryLocation?.mapUrl}
									</a>
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<Modal show={editOffice} onHide={handleClose} centered backdrop="static">
				<div className="common-popup">
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="semibold-16 mb-0">Location</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="common-popup-body">
							<div className="container px-0">
								<div className="row">
									<div className="col-md-6">
										<div className="form-group">
											<label htmlFor="collection">Country</label>
											<CustomDropdown
												type="addItem"
												title={'Country'}
												onItemClick={onChangeCountry}
												items={countries.map((item) => ({
													id: item.id,
													value: item.name,
													key: item.name
												}))}
												selectedItem={
													countries?.find(
														(item) => parseInt(item.id) === parseInt(countryId)
													)?.name
												}
											/>
											{errors.countryError && (
												<p className="error">{errors.countryError}</p>
											)}
										</div>
									</div>
									<div className="col-md-6">
										<div className="form-group">
											<label htmlFor="state">State</label>
											<input
												type="text"
												autoComplete="off"
												id="state"
												name="state"
												value={locationData?.state}
												onChange={(e) => onChnageInfo(e)}
											/>
										</div>
									</div>
									<div className="col-md-6">
										<div className="form-group">
											<label htmlFor="city">City*</label>
											<input
												type="text"
												autoComplete="off"
												id="city"
												name="city"
												value={locationData?.city}
												onChange={(e) => onChnageInfo(e)}
											/>
											{errors.cityError && <p className="error">{errors.cityError}</p>}
										</div>
									</div>
									{/*<div className="col-md-6">*/}
									{/*	<div className="form-group ">*/}
									{/*		<label htmlFor="officeCity">City*</label>*/}
									{/*		<select*/}
									{/*			name="officeCity"*/}
									{/*			id="officeCity"*/}
									{/*			value={cityId}*/}
									{/*			onChange={(e) => onChnageCity(e)}*/}
									{/*		>*/}
									{/*			<option value={''}>Select</option>*/}
									{/*			{locationData &&*/}
									{/*				cities.map((item) => (*/}
									{/*					<option value={item.id} key={item.id}>*/}
									{/*						{item.name}*/}
									{/*					</option>*/}
									{/*				))}*/}
									{/*		</select>*/}
									{/*		{errors.cityError && <p className="error">{errors.cityError}</p>}*/}
									{/*	</div>*/}
									{/*</div>*/}
									<div className="col-md-6">
										<div className="form-group ">
											<label htmlFor="zip">Zip</label>
											<input
												type="text"
												autoComplete="off"
												id="zip"
												name="zip"
												placeholder={'0000'}
												value={locationData?.zip}
												onChange={(e) => onChnageInfo(e)}
											/>
										</div>
									</div>
									<div className="col-md-12">
										<div className="form-group">
											<label htmlFor="mapUrl">Google location link</label>
											<input
												type="text"
												autoComplete="off"
												id="mapUrl"
												name="mapUrl"
												value={locationData?.mapUrl}
												onChange={(e) => onChnageInfo(e)}
											/>
											{errors.validMapUrl && (
												<p className="error">{errors.validMapUrl}</p>
											)}
										</div>
									</div>
									<div className="col-md-12">
										<div className="form-group mb-0">
											<label htmlFor="address">Address</label>
											<textarea
												name="address"
												id="address"
												placeholder="Write address"
												value={locationData?.address}
												onChange={(e) => onChnageInfo(e)}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<div className="common-popup-footer d-flex justify-content-end">
							<div className="footer-buttons text-right">
								<button className="button outline size32" onClick={handleClose}>
									Cancel
								</button>
								<button
									disabled={isSubmitLoading}
									className="button size32 ml-2"
									onClick={onSubmit}
								>
									Submit
								</button>
							</div>
						</div>
					</Modal.Footer>
				</div>
			</Modal>
		</>
	);
};

export default Location;
